import {
  Box,
  VStack,
  Avatar,
  Text,
  Button,
  HStack,
  IconButton,
  useToast,
  Spinner,
} from '@chakra-ui/react';
import { Camera, ArrowLeft } from 'lucide-react';
import { useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { updateWhatsAppProfilePhoto } from '../../../hooks/whatsapp_number';

export default function EditProfilePhotoPage() {
  const navigate = useNavigate();
  const { orgId, whatsappNumberId } = useParams();
  const toast = useToast();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState<string | undefined>();

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.files?.[0];
    if (!selected) {
      return;
    }
    setFile(selected);
    setPreview(URL.createObjectURL(selected));
  };

  const handleSave = async () => {
    if (!orgId || !whatsappNumberId || !file) {
      return;
    }

    setSaving(true);
    const res = await updateWhatsAppProfilePhoto(whatsappNumberId, orgId, file);
    setSaving(false);

    if (res.success) {
      toast({
        title: 'Profile photo updated',
        status: 'success',
        duration: 1500,
      });
      navigate(-1);
      return;
    }

    toast({
      title: 'Update failed',
      description: 'Unable to update profile photo',
      status: 'error',
      duration: 3000,
    });
  };

  return (
    <Box bg="white" minH="100vh">
      <HStack px={4} py={3} justify="space-between">
        <IconButton
          aria-label="Back"
          icon={<ArrowLeft />}
          variant="ghost"
          onClick={() => navigate(-1)}
        />
        <Text fontWeight="semibold">Profile photo</Text>
        {saving ? (
          <Spinner size="sm" />
        ) : (
          <Text
            color={file ? '#25D366' : 'gray.400'}
            cursor={file ? 'pointer' : 'not-allowed'}
            onClick={file ? handleSave : undefined}
          >
            Save
          </Text>
        )}
      </HStack>

      <VStack mt={10} spacing={6}>
        <Avatar size="2xl" bg="green" src={preview} />
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          onChange={handleFileChange}
          style={{ display: 'none' }}
        />
        <Button leftIcon={<Camera />} colorScheme="green" onClick={() => inputRef.current?.click()}>
          Choose photo
        </Button>
        <Text fontSize="sm" color="gray.500">
          Profile photo upload is supported. Removal is not exposed here because this inbox only performs safe Meta-side updates.
        </Text>
      </VStack>
    </Box>
  );
}
